import { Application, Container, Graphics, Sprite, Text, TextStyle } from 'pixi.js';
import { PlaceholderCharacter, centerHorizontally, fitWidth } from '../characters/Character.js';
import { FlipbookCharacter, buildFramePaths } from '../characters/FlipbookCharacter.js';
import { detectLocale, type Locale } from '../story/caseModel.js';

const COPY: Record<Locale, { readonly subtitle: string; readonly enter: string }> = {
  'en-US': {
    subtitle:
      'A neighborhood node will supposedly go dark in ten minutes.\n' +
      'Patch is ready to click. Bit trusts nobody.\n' +
      'They need a third opinion — yours.',
    enter: 'ENTER THE CASE',
  },
  'fr-FR': {
    subtitle:
      'Un nœud de quartier doit être coupé dans dix minutes.\n' +
      'Patch veut agir. Bit ne fait confiance à personne.\n' +
      'Il leur manque un avis — le tien.',
    enter: "ENTRER DANS L'AFFAIRE",
  },
  'zh-CN': {
    subtitle:
      '一个社区节点将在十分钟后断线。\nPatch准备行动，Bit谁也不信。\n他们需要第三个判断——你的判断。',
    enter: '进入案件',
  },
};

/**
 * The game's opening screen.
 *
 * Deliberately reuses the exact copy already approved for the Telegram bot's
 * `/start` flow (see apps/telegram-bot/src/adapters/telegram/bot.ts, `COPY.welcome`
 * for en-US) so the Mini App and the chat bot tell the same story on day one,
 * rather than inventing new lore for this scaffold.
 */
export class IntroScene {
  readonly view = new Container();
  /** The real Patch flipbook (approved cut-out baked from Blender), when its frames are present. */
  private flipbook: FlipbookCharacter | undefined;
  /** Fallback used only when the rendered flipbook frames are not available (e.g. a fresh checkout). */
  private placeholder: PlaceholderCharacter | undefined;
  private onStart: (() => void) | undefined;

  static async create(app: Application): Promise<IntroScene> {
    const scene = new IntroScene();
    await scene.build(app);
    return scene;
  }

  /** Register a callback for when the player taps "Enter the case". */
  setOnStart(callback: () => void): void {
    this.onStart = callback;
  }

  update(deltaMs: number): void {
    // The flipbook self-advances on PixiJS's shared ticker; only the procedural
    // placeholder needs a manual per-frame update.
    this.placeholder?.update(deltaMs);
  }

  /**
   * Loads Patch's real flipbook if its frames exist, otherwise the procedural
   * placeholder. Returns the on-screen sprite either way so the scene can lay
   * it out identically. Keeping the fallback means `pnpm dev` still runs on a
   * checkout where the Blender frames have not been rendered yet.
   */
  private async loadPatch(): Promise<Sprite> {
    try {
      this.flipbook = await FlipbookCharacter.load([
        {
          name: 'idle',
          frameUrls: buildFramePaths('/characters/patch/idle', 24, 3, 'webp'),
          fps: 12,
          loop: true,
        },
        {
          name: 'react-positive',
          frameUrls: buildFramePaths('/characters/patch/react-positive', 18, 3, 'webp'),
          fps: 12,
          loop: false,
        },
        {
          name: 'react-negative',
          frameUrls: buildFramePaths('/characters/patch/react-negative', 18, 3, 'webp'),
          fps: 12,
          loop: false,
        },
      ]);
      return this.flipbook.sprite;
    } catch (error) {
      console.warn('[mini-app] Patch flipbook frames unavailable, using placeholder.', error);
      this.placeholder = await PlaceholderCharacter.load('/characters/patch-placeholder.png');
      return this.placeholder.sprite;
    }
  }

  private async build(app: Application): Promise<void> {
    const copy = COPY[detectLocale()];
    const background = new Graphics()
      .rect(0, 0, app.screen.width, app.screen.height)
      .fill({ color: 0x0b0f10 });
    this.view.addChild(background);

    const patchSprite = await this.loadPatch();
    fitWidth(patchSprite, Math.min(app.screen.width * 0.82, 560));
    patchSprite.x = app.screen.width / 2;
    patchSprite.y = app.screen.height * 0.62;
    this.view.addChild(patchSprite);

    const titleStyle = new TextStyle({
      fill: 0xe8fff2,
      fontFamily: 'sans-serif',
      fontSize: 28,
      fontWeight: '700',
      align: 'center',
      wordWrap: true,
      wordWrapWidth: app.screen.width * 0.85,
    });
    const title = new Text({ text: 'BREAKING HACK', style: titleStyle });
    title.anchor.set(0.5, 0);
    title.x = app.screen.width / 2;
    title.y = app.screen.height * 0.08;
    this.view.addChild(title);

    const subtitleStyle = new TextStyle({
      fill: 0x9fe8c0,
      fontFamily: 'sans-serif',
      fontSize: 15,
      align: 'center',
      wordWrap: true,
      wordWrapWidth: app.screen.width * 0.8,
    });
    const subtitle = new Text({
      text: copy.subtitle,
      style: subtitleStyle,
    });
    subtitle.anchor.set(0.5, 0);
    subtitle.x = app.screen.width / 2;
    subtitle.y = title.y + 48;
    this.view.addChild(subtitle);

    const button = new Graphics()
      .roundRect(0, 0, 240, 56, 28)
      .fill({ color: 0x1f6f4a })
      .stroke({ color: 0x9fe8c0, width: 2 });
    button.x = app.screen.width / 2 - 120;
    button.y = app.screen.height * 0.86;
    button.eventMode = 'static';
    button.cursor = 'pointer';
    button.on('pointertap', () => {
      // Patch reacts on screen, then settles back to idle on its own.
      this.flipbook?.play('react-positive');
      this.onStart?.();
    });
    this.view.addChild(button);

    const buttonLabel = new Text({
      text: copy.enter,
      style: new TextStyle({
        fill: 0xe8fff2,
        fontFamily: 'sans-serif',
        fontSize: copy.enter.length > 18 ? 13 : 15,
        fontWeight: '700',
        letterSpacing: 1,
      }),
    });
    buttonLabel.anchor.set(0.5);
    buttonLabel.x = button.x + button.width / 2;
    buttonLabel.y = button.y + button.height / 2;
    this.view.addChild(buttonLabel);

    centerHorizontally(patchSprite, app);
  }

  destroy(): void {
    // The character sprite is a child of view; children:true destroys it.
    this.view.destroy({ children: true });
  }
}
